import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldAlert, RefreshCw, Save } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Sportsbook Risk Management — editable launch limits protecting the bankroll.
 * Nothing here is hard-coded; changes take effect immediately for new wagers.
 */
export const SportsRiskPanel = () => {
    const [cfg, setCfg] = useState(null);
    const [exposure, setExposure] = useState(null);
    const [liveExp, setLiveExp] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [c, e, le] = await Promise.all([
                axios.get(`${API}/admin/sports-risk`, { withCredentials: true }),
                axios.get(`${API}/admin/sports-exposure`, { withCredentials: true }).catch(() => ({ data: null })),
                axios.get(`${API}/admin/sports-exposure/live`, { withCredentials: true }).catch(() => ({ data: null })),
            ]);
            setCfg(c.data);
            setExposure(e.data);
            setLiveExp(le.data);
        } catch { toast.error('Could not load sportsbook limits'); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    // Real-time refresh of live in-play exposure every 15s + exposure-push toast
    useEffect(() => {
        const id = setInterval(async () => {
            try {
                const { data } = await axios.get(`${API}/admin/sports-exposure/live`, { withCredentials: true });
                setLiveExp(data);
                (data.new_alerts || []).forEach((a) => {
                    toast.error(`⚠️ Live exposure: ${a.match} at $${Number(a.liability).toFixed(0)} — over the cap`, { duration: 8000 });
                });
            } catch {}
        }, 15000);
        return () => clearInterval(id);
    }, []);

    const suspendGame = async (eventId, match, suspended) => {
        try {
            await axios.post(`${API}/admin/sports/event-suspend`,
                { event_id: eventId, suspended, match }, { withCredentials: true });
            toast.success(suspended ? 'Live betting suspended on this game' : 'Live betting resumed');
            const { data } = await axios.get(`${API}/admin/sports-exposure/live`, { withCredentials: true });
            setLiveExp(data);
        } catch { toast.error('Could not update game suspension'); }
    };

    const setNum = (section, key, val) =>
        setCfg((c) => ({ ...c, [section]: { ...c[section], [key]: val === '' ? '' : Number(val) } }));

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                straight: cfg.straight, prop: cfg.prop, parlay: cfg.parlay, sgp: cfg.sgp,
                global: cfg.global, dynamic_limits_enabled: cfg.dynamic_limits_enabled,
                bankroll_tier: cfg.bankroll_tier,
            };
            const { data } = await axios.patch(`${API}/admin/sports-risk`, payload, { withCredentials: true });
            setCfg(data);
            toast.success('Sportsbook limits saved');
        } catch { toast.error('Could not save limits'); }
        finally { setSaving(false); }
    };

    if (loading || !cfg) {
        return <div className="rounded-2xl border border-white/10 bg-[#0b0b0f] p-5 mt-6 text-white/50 text-sm" data-testid="sports-risk-loading">Loading sportsbook limits…</div>;
    }

    const Row = ({ label, section, k, prefix = '$' }) => (
        <div className="flex items-center justify-between gap-3 py-1.5">
            <span className="text-xs text-white/70">{label}</span>
            <div className="flex items-center gap-1">
                <span className="text-white/40 text-xs">{prefix}</span>
                <input type="number" min="0" value={cfg[section]?.[k] ?? ''}
                    onChange={(e) => setNum(section, k, e.target.value)}
                    className="w-24 bg-black/40 border border-white/15 rounded-lg px-2 py-1 text-white text-sm text-right"
                    data-testid={`sr-${section}-${k}`} />
            </div>
        </div>
    );

    return (
        <div className="rounded-2xl border border-[#FFD700]/25 bg-[#12100a] p-4 sm:p-5 mt-6" data-testid="sports-risk-panel">
            <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#FFD700]/15 flex items-center justify-center shrink-0">
                        <ShieldAlert className="w-5 h-5 text-[#FFD700]" />
                    </div>
                    <div>
                        <h3 className="text-base font-black text-[#FFD700] tracking-wide">SPORTSBOOK RISK MANAGEMENT</h3>
                        <p className="text-[11px] text-white/50 max-w-xl">Launch limits for the ~$20k bankroll. Every wager is checked against these before it's accepted. Raise them as the bankroll grows.</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={load} className="p-2 rounded-lg hover:bg-white/10 text-white/60" data-testid="sr-refresh"><RefreshCw className="w-4 h-4" /></button>
                    <button onClick={save} disabled={saving} className="flex items-center gap-1.5 rounded-lg bg-[#FFD700] text-black font-black text-xs px-3 py-2 hover:brightness-110 disabled:opacity-40" data-testid="sr-save"><Save className="w-4 h-4" /> Save</button>
                </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
                <Card title="Straight bets" note="Moneylines · Spreads · Totals">
                    <Row label="Max wager" section="straight" k="max_wager" />
                    <Row label="Max payout / ticket" section="straight" k="max_payout" />
                </Card>
                <Card title="Player props">
                    <Row label="Max wager" section="prop" k="max_wager" />
                    <Row label="Max payout / ticket" section="prop" k="max_payout" />
                </Card>
                <Card title="Standard parlays">
                    <Row label="Max wager" section="parlay" k="max_wager" />
                    <Row label="Max payout / ticket" section="parlay" k="max_payout" />
                    <Row label="Min legs" section="parlay" k="min_legs" prefix="#" />
                    <Row label="Max legs" section="parlay" k="max_legs" prefix="#" />
                </Card>
                <Card title="Same game parlays">
                    <Row label="Max wager" section="sgp" k="max_wager" />
                    <Row label="Max payout / ticket" section="sgp" k="max_payout" />
                </Card>
                <Card title="Global risk limits" wide>
                    <Row label="Max payout on ANY single ticket" section="global" k="max_ticket_payout" />
                    <Row label="Max payout per customer / day" section="global" k="max_daily_customer_payout" />
                </Card>
            </div>

            <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.02] p-3 flex items-center justify-between gap-3">
                <div>
                    <div className="text-xs font-bold text-white">Dynamic bankroll-tier limits</div>
                    <div className="text-[11px] text-white/40">When off, limits never auto-increase. Tiers: under $25k · $25–50k · $50–100k · $100k+ (Phase 2)</div>
                </div>
                <label className="flex items-center gap-2 text-xs text-white/70 cursor-pointer shrink-0">
                    <input type="checkbox" checked={!!cfg.dynamic_limits_enabled}
                        onChange={(e) => setCfg((c) => ({ ...c, dynamic_limits_enabled: e.target.checked }))}
                        data-testid="sr-dynamic-enabled" />
                    Enabled
                </label>
            </div>

            {cfg.dynamic_limits_enabled && (
                <div className="mt-2 flex items-center gap-2 text-xs" data-testid="sr-tier-row">
                    <span className="text-white/50">Active tier:</span>
                    <select value={cfg.bankroll_tier || 'under_25k'}
                        onChange={(e) => setCfg((c) => ({ ...c, bankroll_tier: e.target.value }))}
                        className="bg-black/40 border border-white/15 rounded-lg px-2 py-1 text-white" data-testid="sr-tier-select">
                        <option value="under_25k">Under $25k</option>
                        <option value="25k_50k">$25k–$50k</option>
                        <option value="50k_100k">$50k–$100k</option>
                        <option value="over_100k">$100k+</option>
                    </select>
                    <span className="text-white/30">Save to apply — limits above update to the tier. No auto-increase.</span>
                </div>
            )}

            {/* Live in-play exposure — real-time liability on games underway */}
            {liveExp && (
                <div className="mt-5" data-testid="sports-live-exposure">
                    <div className="flex items-center gap-2 mb-2">
                        <span className="relative flex h-2.5 w-2.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF3B30] opacity-75" />
                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#FF3B30]" />
                        </span>
                        <span className="text-[11px] font-black uppercase tracking-widest text-white/60">
                            Live now — in-play liability ({liveExp.live_game_count} game{liveExp.live_game_count === 1 ? '' : 's'})
                        </span>
                    </div>
                    {liveExp.has_alert && (
                        <div className="flex items-center gap-2 rounded-xl border border-[#FF3B30]/50 bg-[#FF3B30]/10 px-3 py-2 mb-3" data-testid="live-exposure-alert">
                            <ShieldAlert className="w-4 h-4 text-[#FF3B30] shrink-0" />
                            <span className="text-xs font-bold text-[#FF3B30]">
                                Live exposure alert — one or more in-play games exceed the ${Number(liveExp.alert_threshold).toFixed(0)} per-game cap. Review below.
                            </span>
                        </div>
                    )}
                    <div className="grid grid-cols-3 gap-2 mb-3 text-center">
                        <Card title="Live liability"><div className="font-mono font-black text-[#ff5470] text-sm">${Number(liveExp.total.liability).toFixed(2)}</div></Card>
                        <Card title="Live tickets"><div className="font-mono font-black text-white text-sm">{liveExp.total.tickets}</div></Card>
                        <Card title="Live stake"><div className="font-mono font-black text-[#39FF14] text-sm">${Number(liveExp.total.stake).toFixed(2)}</div></Card>
                    </div>
                    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-white/40 mb-1.5">By live game</div>
                        {(!liveExp.games || liveExp.games.length === 0) ? (
                            <div className="text-white/30 text-xs">No open bets on games currently in-play.</div>
                        ) : (
                            <div className="space-y-2">
                                {liveExp.games.map((g, i) => (
                                    <div key={i} className={`rounded px-1.5 py-1.5 ${g.alert ? 'bg-[#FF3B30]/10' : ''}`} data-testid={`live-exposure-game-${g.event_id}`}>
                                        <div className="flex items-center justify-between gap-2 text-xs">
                                            <span className="text-white/70 truncate max-w-[58%]">
                                                {g.alert && <span className="text-[#FF3B30] mr-1">●</span>}
                                                {g.match}
                                                {(g.home_score != null && g.away_score != null) && <span className="text-white/40 font-mono"> · {g.away_score}–{g.home_score}</span>}
                                                <span className="text-white/30"> · {g.tickets} tix · {g.customers} cust</span>
                                                {g.suspended && <span className="ml-1 text-[9px] font-black text-[#FFB020] bg-[#FFB020]/15 px-1 rounded">SUSPENDED</span>}
                                            </span>
                                            <span className={`font-mono font-bold ${g.alert ? 'text-[#FF3B30]' : 'text-[#ff5470]'}`}>${Number(g.liability).toFixed(2)}</span>
                                        </div>
                                        {(g.alert || g.suspended) && (
                                            <div className="mt-1.5 flex items-center justify-between gap-2 rounded-lg border border-[#FFB020]/30 bg-[#FFB020]/[0.06] px-2 py-1.5">
                                                <span className="text-[10px] text-[#FFB020] font-semibold leading-snug">
                                                    {g.suspended
                                                        ? 'Live betting is suspended on this game.'
                                                        : `Over cap by $${Number(g.over_cap).toFixed(0)} — suspend new bets or trim exposure to hedge.`}
                                                </span>
                                                <button
                                                    onClick={() => suspendGame(g.event_id, g.match, !g.suspended)}
                                                    className={`shrink-0 text-[10px] font-black px-2 py-1 rounded ${g.suspended ? 'bg-[#39FF14] text-black hover:bg-[#39FF14]/80' : 'bg-[#FF3B30] text-white hover:bg-[#FF3B30]/80'}`}
                                                    data-testid={`suspend-game-${g.event_id}`}
                                                >
                                                    {g.suspended ? 'Resume' : 'Suspend'}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* All open exposure (pre-match + live) */}
            {exposure && (
                <div className="mt-5" data-testid="sports-exposure">
                    <div className="text-[11px] font-black uppercase tracking-widest text-white/40 mb-2">All open exposure — potential payout if outcomes win</div>
                    <div className="grid grid-cols-3 gap-2 mb-3 text-center">
                        <Card title="Total liability"><div className="font-mono font-black text-[#ff5470] text-sm">${Number(exposure.total.liability).toFixed(2)}</div></Card>
                        <Card title="Open tickets"><div className="font-mono font-black text-white text-sm">{exposure.total.tickets}</div></Card>
                        <Card title="Stake held"><div className="font-mono font-black text-[#39FF14] text-sm">${Number(exposure.total.stake).toFixed(2)}</div></Card>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <ExpList title="Combined per outcome (multi-customer risk)" rows={exposure.by_outcome_combined}
                            fmt={(r) => `${(r.key?.outcome) || '—'} — ${r.key?.match || ''}`} showCust />
                        <ExpList title="By game" rows={exposure.by_game} fmt={(r) => r.key?.match || r.key?.event_id} showCust />
                        <ExpList title="By customer" rows={exposure.by_customer} fmt={(r) => r.label || r.key} />
                        <ExpList title="By market" rows={exposure.by_market} fmt={(r) => r.key} showCust />
                    </div>
                </div>
            )}
        </div>
    );
};

const Card = ({ title, note, children, wide }) => (
    <div className={`rounded-xl border border-white/10 bg-white/[0.03] p-3 ${wide ? 'sm:col-span-2' : ''}`}>
        <div className="text-[11px] font-black uppercase tracking-wider text-white/50">{title}</div>
        {note && <div className="text-[10px] text-white/30 mb-1">{note}</div>}
        <div className="mt-1">{children}</div>
    </div>
);

const ExpList = ({ title, rows, fmt, showCust }) => (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-white/40 mb-1.5">{title}</div>
        {(!rows || rows.length === 0) ? <div className="text-white/30 text-xs">No open exposure.</div> : (
            <div className="space-y-1">
                {rows.slice(0, 6).map((r, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 text-xs">
                        <span className="text-white/70 truncate max-w-[62%]">{fmt(r)}{showCust && <span className="text-white/30"> · {r.customers} cust</span>}</span>
                        <span className="font-mono text-[#ff5470] font-bold">${Number(r.liability).toFixed(2)}</span>
                    </div>
                ))}
            </div>
        )}
    </div>
);

export default SportsRiskPanel;
